import React, { useState, useEffect, useRef } from 'react';
import {
  collection,
  getDocs,
  doc,
  writeBatch,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL
} from "firebase/storage";
import { db } from '../firebase';
import {
  Box,
  Typography,
  Container,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Paper,
  Grid,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Snackbar,
  Tooltip,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  CopyAll as CopyIcon,
  UploadFile as UploadIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Tu helper formatDate (sin cambios)
const formatDate = (dateValue) => {
  try {
    let timestamp;
    if (!dateValue) return '—';
    if (dateValue.seconds !== undefined && dateValue.nanoseconds !== undefined) {
      timestamp = dateValue.seconds * 1000;
    } else if (dateValue instanceof Date) {
      timestamp = dateValue.getTime();
    } else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      const d = new Date(dateValue);
      if (isNaN(d.getTime())) return 'Fecha inválida';
      timestamp = d.getTime();
    } else {
      return 'Formato desconocido';
    }
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return 'Fecha inválida';
    return d.toLocaleDateString('es-ES');
  } catch (err) {
    console.warn('Error formateando fecha:', dateValue, err);
    return 'Error en fecha';
  }
};

// 🔑 Bancos predefinidos
const BANKS = [
  { id: 'bhd', name: 'BHD León', account: '1234567890', holder: 'S.R.D. COMUNIDAD LOTERIA' },
  { id: 'popular', name: 'Banco Popular', account: '0987654321', holder: 'S.R.D. COMUNIDAD LOTERIA' },
  { id: 'banreservas', name: 'Banreservas', account: '9600560725', holder: 'Marcos Valerio' },
  { id: 'scotia', name: 'Scotiabank', account: '1122334455', holder: 'S.R.D. COMUNIDAD LOTERIA' },
];

function GranRifa() {
  // === Estados principales ===
  const [participants, setParticipants] = useState([]);
  const [countdown, setCountdown] = useState({
    days: '00',
    hours: '00',
    minutes: '00',
    seconds: '00',
  });
  const [isRaffleActive, setIsRaffleActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // === Estados nuevos para el formulario de compra ===
  const [openBuyModal, setOpenBuyModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    quantity: 1,
    bankId: '',
    proofFile: null,
  });
  const [userPhotoFile, setUserPhotoFile] = useState(null); // ✅ NUEVO
  const [errors, setErrors] = useState({});
  const [copied, setCopied] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);
  const userPhotoInputRef = useRef(null); // ✅ NUEVO

  // === useEffects (sin cambios en lógica, solo carga de `userPhotoUrl`) ===
  useEffect(() => {
    const fetchParticipants = async () => {
      try {
        setLoading(true);
        setError(null);
        const snapshot = await getDocs(collection(db, 'granRifa'));
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const groupedData = data.reduce((acc, participant) => {
          const username = participant.username?.trim() || 'Usuario desconocido';
          const rawDate = participant.purchaseDate;
          const purchaseDateStr = formatDate(rawDate);
          if (!acc[username]) {
            acc[username] = {
              username,
              ticketCount: 0,
              purchaseDates: new Set(),
              // Tomar la primera foto y estado de aprobación (asumimos que son iguales por usuario)
              userPhotoUrl: participant.userPhotoUrl || null,
              isApproved: participant.isApproved || false,
            };
          }
          acc[username].ticketCount += 1;
          acc[username].purchaseDates.add(purchaseDateStr);
          return acc;
        }, {});
        const result = Object.values(groupedData).map((item) => ({
          ...item,
          purchaseDates: Array.from(item.purchaseDates),
        }));
        const sortedData = result.sort((a, b) => b.ticketCount - a.ticketCount);
        setParticipants(sortedData);
      } catch (err) {
        console.error('Error crítico al cargar participantes:', err);
        setError('No se pudieron cargar los datos. Por favor, recarga la página.');
      } finally {
        setLoading(false);
      }
    };
    fetchParticipants();
  }, []);

  useEffect(() => {
    const targetDate = new Date('2025-12-22T21:30:00-04:00');
    if (isNaN(targetDate.getTime())) {
      console.error('Fecha objetivo inválida');
      return;
    }

    const interval = setInterval(() => {
      const now = new Date();
      const timeLeft = targetDate - now;

      if (timeLeft <= 0) {
        clearInterval(interval);
        setIsRaffleActive(true);
      } else {
        const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeLeft / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((timeLeft / (1000 * 60)) % 60);
        const seconds = Math.floor((timeLeft / 1000) % 60);

        setCountdown({
          days: String(days).padStart(2, '0'),
          hours: String(hours).padStart(2, '0'),
          minutes: String(minutes).padStart(2, '0'),
          seconds: String(seconds).padStart(2, '0'),
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // === Handlers ===
  const handleWhatsappClick = () => {
    const whatsappUrl =
      'https://wa.me/18096245631?text=Hola,%20quiero%20saber%20cómo%20puedo%20participar%20en%20la%20rifa.';
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handleHomeRedirect = () => {
    navigate('/');
  };

  const handleOpenBuyModal = () => {
    setOpenBuyModal(true);
    setErrors({});
  };

  const handleCloseBuyModal = () => {
    setOpenBuyModal(false);
    setFormData({ name: '', quantity: 1, bankId: '', proofFile: null });
    setUserPhotoFile(null); // ✅ reset
    setErrors({});
    setCopied(false);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({ ...prev, proofFile: 'Formato no válido. Usa JPG, PNG o PDF.' }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, proofFile: 'El archivo no debe superar 5 MB.' }));
        return;
      }
      setFormData((prev) => ({ ...prev, proofFile: file }));
      setErrors((prev) => ({ ...prev, proofFile: '' }));
    }
  };

  const handleCopyAccount = (account) => {
    navigator.clipboard.writeText(account).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'El nombre es obligatorio.';
    if (!formData.quantity || formData.quantity < 1) newErrors.quantity = 'Selecciona al menos 1 boleto.';
    if (!formData.bankId) newErrors.bankId = 'Selecciona un banco.';
    if (!formData.proofFile) newErrors.proofFile = 'Sube tu comprobante de pago.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);

      const storage = getStorage();

      // 📌 1. Subir comprobante
      const fileName = `comprobantes/${Date.now()}_${formData.proofFile.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, formData.proofFile);
      const downloadURL = await getDownloadURL(storageRef);

      // 📌 2. Subir foto del usuario (si existe)
      let userPhotoURL = null;
      if (userPhotoFile) {
        const photoName = `fotos-usuarios/${Date.now()}_${userPhotoFile.name}`;
        const photoRef = ref(storage, photoName);
        await uploadBytes(photoRef, userPhotoFile);
        userPhotoURL = await getDownloadURL(photoRef);
      }

      // 📌 3. Guardar boletos
      const batch = writeBatch(db);
      const ticketsToCreate = Number(formData.quantity);

      for (let i = 0; i < ticketsToCreate; i++) {
        const newDocRef = doc(collection(db, "granRifa"));
        batch.set(newDocRef, {
          username: formData.name.trim(),
          bankId: formData.bankId,
          proofUrl: downloadURL,
          userPhotoUrl: userPhotoURL, // ✅ incluir
          purchaseDate: new Date(),
          isApproved: false,
          approvedAt: null,
        });
      }

      await batch.commit();

      setSnackbar({
        open: true,
        message: '¡Gracias por tu compra! 🎉 Tu pago está siendo procesado.',
        severity: 'success',
      });
      handleCloseBuyModal();
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: 'Error al enviar tu compra. Intenta nuevamente.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const total = formData.quantity * 500;

  // === Componentes reutilizables ===
  const renderCountdown = () => (
    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#e8f5e9', borderRadius: '12px', border: '2px solid #4caf50' }}>
      <Typography variant="h6" sx={{ color: '#1b5e20', fontWeight: 'bold', mb: 2 }}>
        ⏳ Tiempo restante
      </Typography>
      <Grid container spacing={isMobile ? 1 : 2} justifyContent="center">
        {Object.entries(countdown).map(([unit, value]) => (
          <Grid item xs={3} sm={1.5} key={unit}>
            <Paper elevation={3} sx={{ p: 1, textAlign: 'center', backgroundColor: '#4caf50', color: '#fff' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {value}
              </Typography>
              <Typography variant="caption" sx={{ textTransform: 'uppercase' }}>
                {unit === 'days' ? 'Días' : unit === 'hours' ? 'Hrs' : unit === 'minutes' ? 'Min' : 'Seg'}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderDesktopTable = () => (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#4caf50' }}>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold', minWidth: 220 }}>Participante</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center', minWidth: 100 }}>Boletos</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold', minWidth: 200 }}>Fechas de compra</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {participants.length > 0 ? (
            participants.map((participant, index) => (
              <TableRow
                key={`${participant.username}-${index}`}
                sx={{
                  '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                  '&:nth-of-type(even)': { backgroundColor: '#e8f5e9' },
                  '&:hover': { backgroundColor: '#d7ffd9' },
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {participant.userPhotoUrl ? (
                      <Box
                        component="img"
                        src={participant.userPhotoUrl}
                        alt={participant.username}
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '2px solid #4caf50',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          bgcolor: '#e0e0e0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 18, color: '#757575' }} />
                      </Box>
                    )}
                    <Box>
                      <Typography sx={{ fontWeight: 'medium', fontSize: '1rem' }}>
                        {participant.username}
                      </Typography>
                      {participant.isApproved && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.3 }}>
                          <CheckIcon sx={{ fontSize: 14, color: '#4caf50', mr: 0.5 }} />
                          <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                            Aprobado
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', color: '#1b5e20' }}>
                  {participant.ticketCount}
                </TableCell>
                <TableCell>{participant.purchaseDates.join(', ')}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body1" color="text.secondary">🕊️ Aún no hay participantes registrados.</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );

  const renderMobileList = () => (
    <Stack spacing={2}>
      {participants.length > 0 ? (
        participants.map((participant, index) => (
          <Paper
            key={`${participant.username}-${index}`}
            elevation={2}
            sx={{
              p: 2,
              borderLeft: '5px solid #4caf50',
              backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#e8f5e9',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {participant.userPhotoUrl ? (
                <Box
                  component="img"
                  src={participant.userPhotoUrl}
                  alt={participant.username}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #4caf50',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: '#e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PersonIcon sx={{ fontSize: 16, color: '#757575' }} />
                </Box>
              )}
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1b5e20', flex: 1 }}>
                {participant.username}
              </Typography>
              {participant.isApproved && (
                <CheckIcon sx={{ fontSize: 18, color: '#4caf50' }} />
              )}
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1b5e20', textAlign: 'center', mb: 1 }}>
              {participant.ticketCount} 🎟️
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Fechas:</strong> {participant.purchaseDates.join(', ')}
            </Typography>
          </Paper>
        ))
      ) : (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body1" color="text.secondary">🕊️ Aún no hay participantes registrados.</Typography>
        </Box>
      )}
    </Stack>
  );

  // === Renderizado ===
  return (
    <Container maxWidth="md" sx={{ py: 3, position: 'relative', minHeight: '100vh' }}>
      <Stack spacing={3}>
        <Typography variant="h4" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          Entra a la tombola y cambia tu diciembre
        </Typography>

        {isRaffleActive ? (
          <Alert severity="success" variant="filled" sx={{ justifyContent: 'center', fontSize: '1.2rem', fontWeight: 'bold' }}>
            ¡La Gran Rifa ha comenzado! ¡Mucha suerte a todos!
          </Alert>
        ) : (
          renderCountdown()
        )}

        {/* ✅ BOTÓN LLAMATIVO DE COMPRA */}
        <Box sx={{ textAlign: 'center' }}>
          <Button
            onClick={handleOpenBuyModal}
            variant="contained"
            size="large"
            startIcon={<span>🎟️</span>}
            sx={{
              backgroundColor: '#2e7d32',
              color: '#fff',
              fontWeight: 'bold',
              fontSize: { xs: '16px', sm: '18px' },
              px: 4,
              py: 1.5,
              borderRadius: '12px',
              boxShadow: '0 6px 16px rgba(46, 125, 50, 0.4)',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.03)' },
                '100%': { transform: 'scale(1)' },
              },
              '&:hover': {
                backgroundColor: '#1b5e20',
                transform: 'scale(1.05)',
                boxShadow: '0 8px 20px rgba(46, 125, 50, 0.5)',
              },
            }}
          >
            🎟️ Comprar Boletos Ahora
          </Button>
        </Box>

        {/* WhatsApp info button */}
        <Box sx={{ textAlign: 'center' }}>
          <Button
            onClick={handleWhatsappClick}
            variant="contained"
            sx={{
              backgroundColor: '#25D366',
              color: '#fff',
              fontWeight: 'bold',
              fontSize: { xs: '14px', sm: '16px' },
              padding: '12px 24px',
              '&:hover': { backgroundColor: '#1ebe5d' },
            }}
          >
            📲 Solicitar información por WhatsApp
          </Button>
        </Box>

        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            background: 'linear-gradient(90deg, #ffe082, #ffd54f)',
            color: '#1b5e20',
            padding: '20px',
            borderRadius: '12px',
            fontWeight: 'bold',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            border: '2px solid #f57f17',
            fontSize: { xs: '16px', sm: '18px' },
            lineHeight: 1.6,
          }}
        >
          🎉 <strong>¡Aprovecha esta gran oportunidad!</strong> Inscríbete por tan solo <strong>RD$500 pesos</strong> y obtén un boleto para la rifa de <strong>RD$100,000 pesos</strong>.
          📅 Sorteo en vivo: <strong>Lunes 22 de diciembre 2025, 9:30 PM</strong>. ✨{' '}
          <span style={{ color: '#d32f2f' }}>¡Conéctate, participa y gana!</span> ✨
        </Typography>

        {loading && (
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2, color: 'text.secondary' }}>Cargando participantes...</Typography>
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && (
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                textAlign: 'center',
                mb: 2,
                color: '#1b5e20',
                textTransform: 'uppercase',
              }}
            >
              👥 Usuarios que han comprado sus boletos
            </Typography>
            {isMobile ? renderMobileList() : renderDesktopTable()}
          </Box>
        )}

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            onClick={handleHomeRedirect}
            variant="outlined"
            sx={{
              borderColor: '#1976d2',
              color: '#1976d2',
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              '&:hover': { backgroundColor: '#e3f2fd' },
            }}
          >
            🏠 Volver al inicio
          </Button>
        </Box>
      </Stack>

      {/* ✅ MODAL DE COMPRA con foto del usuario */}
      <Dialog
        open={openBuyModal}
        onClose={handleCloseBuyModal}
        maxWidth="sm"
        fullWidth
        scroll="body"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', color: '#2e7d32' }}>
          🎫 Compra de Boletos
        </DialogTitle>
        <IconButton
          aria-label="cerrar"
          onClick={handleCloseBuyModal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent>
          <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
            {/* Nombre */}
            <TextField
              fullWidth
              label="📝 Nombre completo"
              placeholder="Ingresa tu nombre completo"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              margin="normal"
              variant="outlined"
              sx={{ mb: 2 }}
            />

            {/* Cantidad */}
            <FormControl fullWidth margin="normal" sx={{ mb: 2 }}>
              <InputLabel id="cantidad-label">🔢 Cantidad de boletos</InputLabel>
              <Select
                labelId="cantidad-label"
                value={formData.quantity}
                label="🔢 Cantidad de boletos"
                onChange={(e) => handleChange('quantity', Number(e.target.value))}
                error={!!errors.quantity}
              >
                {[1, 2, 3, 5, 10].map((num) => (
                  <MenuItem key={num} value={num}>
                    {num} boleto{num > 1 ? 's' : ''}
                  </MenuItem>
                ))}
              </Select>
              {errors.quantity && (
                <Typography color="error" variant="caption">
                  {errors.quantity}
                </Typography>
              )}
            </FormControl>

            {/* Total */}
            <Card
              sx={{
                mb: 2,
                backgroundColor: '#e8f5e9',
                border: '1px solid #81c784',
                borderRadius: '8px',
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1b5e20', textAlign: 'center' }}>
                  💰 Total: ${total.toLocaleString()} Pesos
                </Typography>
              </CardContent>
            </Card>

            {/* Bancos */}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#2e7d32' }}>
              🏦 Selecciona tu Banco para Pagar
            </Typography>
            <FormControl fullWidth margin="normal" sx={{ mb: 2 }}>
              <InputLabel id="banco-label">Banco</InputLabel>
              <Select
                labelId="banco-label"
                value={formData.bankId}
                label="Banco"
                onChange={(e) => handleChange('bankId', e.target.value)}
                error={!!errors.bankId}
              >
                <MenuItem value="">— Selecciona —</MenuItem>
                {BANKS.map((bank) => (
                  <MenuItem key={bank.id} value={bank.id}>
                    {bank.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.bankId && (
                <Typography color="error" variant="caption">
                  {errors.bankId}
                </Typography>
              )}
            </FormControl>

            {/* Datos del banco */}
            {formData.bankId && (
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: '#f1f8e9',
                  border: '1px solid #a5d6a7',
                  borderRadius: '8px',
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {BANKS.find((b) => b.id === formData.bankId)?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Número:</strong> {BANKS.find((b) => b.id === formData.bankId)?.account}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Titular:</strong> {BANKS.find((b) => b.id === formData.bankId)?.holder}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Tooltip title={copied ? '¡Copiado!' : 'Copiar número'}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleCopyAccount(BANKS.find((b) => b.id === formData.bankId)?.account)
                      }
                      sx={{
                        backgroundColor: copied ? '#4caf50' : '#e0e0e0',
                        color: copied ? '#fff' : '#000',
                        '&:hover': { backgroundColor: copied ? '#388e3c' : '#bdbdbd' },
                      }}
                    >
                      {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            )}

            {/* Subir Comprobante */}
            <Box
              sx={{
                border: '2px dashed',
                borderColor: errors.proofFile ? 'error.main' : '#9ccc65',
                borderRadius: '8px',
                p: 3,
                textAlign: 'center',
                backgroundColor: errors.proofFile ? '#ffebee' : '#f1f8e9',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  borderColor: '#4caf50',
                  backgroundColor: '#e8f5e9',
                },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadIcon sx={{ fontSize: 40, color: errors.proofFile ? '#d32f2f' : '#4caf50' }} />
              <Typography variant="body1" sx={{ mt: 1, fontWeight: 'medium' }}>
                📤 Sube tu comprobante de pago (voucher)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Formatos: JPG, PNG, PDF (máx. 5 MB)
              </Typography>
              {errors.proofFile && (
                <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                  {errors.proofFile}
                </Typography>
              )}
              {formData.proofFile && (
                <Typography variant="body2" sx={{ mt: 1, color: '#2e7d32', fontWeight: 'bold' }}>
                  ✅ Archivo: {formData.proofFile.name}
                </Typography>
              )}
            </Box>
            <input
              type="file"
              ref={fileInputRef}
              accept=".jpg,.jpeg,.png,.pdf"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {/* ✅ Subir Foto del Usuario (NUEVO) */}
            <Box
              sx={{
                border: '2px dashed',
                borderColor: '#9ccc65',
                borderRadius: '8px',
                p: 3,
                textAlign: 'center',
                backgroundColor: '#f1f8e9',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  borderColor: '#4caf50',
                  backgroundColor: '#e8f5e9',
                },
                mt: 2,
              }}
              onClick={() => userPhotoInputRef.current?.click()}
            >
              <UploadIcon sx={{ fontSize: 40, color: '#4caf50' }} />
              <Typography variant="body1" sx={{ mt: 1, fontWeight: 'medium' }}>
                📸 Sube tu foto (opcional, <strong>aparece en la transmisión</strong>)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Solo JPG/PNG (máx. 2 MB)
              </Typography>
              {userPhotoFile && (
                <Typography variant="body2" sx={{ mt: 1, color: '#2e7d32', fontWeight: 'bold' }}>
                  ✅ Foto: {userPhotoFile.name}
                </Typography>
              )}
            </Box>
            <input
              type="file"
              ref={userPhotoInputRef}
              accept="image/jpeg,image/png"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  if (!['image/jpeg', 'image/png'].includes(file.type)) {
                    setErrors((prev) => ({ ...prev, userPhoto: 'Solo JPG o PNG.' }));
                    return;
                  }
                  if (file.size > 2 * 1024 * 1024) {
                    setErrors((prev) => ({ ...prev, userPhoto: 'Máx. 2 MB.' }));
                    return;
                  }
                  setUserPhotoFile(file);
                  // Limpiar error si existe
                  setErrors((prev) => ({ ...prev, userPhoto: '' }));
                }
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="large"
            startIcon={<CheckIcon />}
            sx={{
              backgroundColor: '#2e7d32',
              color: '#fff',
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              '&:hover': { backgroundColor: '#1b5e20' },
            }}
          >
            ✅ Enviar Pedido y Comprobante
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', fontSize: '1.1rem', fontWeight: 'bold' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* BOTÓN FIJO WHATSAPP */}
      <Tooltip title="💬 ¿Dudas? Escríbenos por WhatsApp" arrow>
        <IconButton
          onClick={handleWhatsappClick}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            backgroundColor: '#25D366',
            color: '#fff',
            width: 60,
            height: 60,
            borderRadius: '50%',
            boxShadow: '0 4px 12px rgba(37, 211, 102, 0.5)',
            '&:hover': {
              backgroundColor: '#1ebe5d',
              transform: 'scale(1.1)',
            },
            zIndex: 1300,
          }}
        >
          <WhatsAppIcon fontSize="large" />
        </IconButton>
      </Tooltip>
    </Container>
  );
}

export default GranRifa;